/**
 * Lottie Animation Manager
 * Single source of truth for all lottie animations
 * 
 * To add a new animation:
 * 1. Add your .ts file to lib/lottie/icons/ (e.g., my-icon.ts)
 * 2. Export your animation like: export const myIcon = { ...lottieData }
 * 3. Add the name to LottieAnimationName type below
 * 4. Import it and add it to normalizationMap
 */

// Add your lottie animation names here for type safety
export type LottieAnimationName = 
    | 'send'
    | 'bookmark'
    | 'bookmark-add'
    | 'bookmark-remove'
    | 'paperclip'
    | 'sidebar'
    | 'error404'
    | 'file'
    | 'validate'
    | 'scroll'
    | 'chevron-down'
    | 'edit'
    | 'trash'
    | 'menu'
    | 'lifebuoy'
    | 'feedback'
    | 'support'
    // Add more animation names here as you add files

// Import your animation data here
import { send } from '@/lib/lottie/icons/send'
import { bookmark } from '@/lib/lottie/icons/bookmark'
import { bookmarkAdd } from '@/lib/lottie/icons/bookmark-add'
import { bookmarkRemove } from '@/lib/lottie/icons/bookmark-remove'
import { paperclip } from '@/lib/lottie/icons/paperclip'
import { sidebar } from '@/lib/lottie/icons/sidebar'
import { error404 } from '@/lib/lottie/icons/findweet/error404'
import { file } from '@/lib/lottie/icons/findweet/file'
import { validate } from '@/lib/lottie/icons/findweet/validate'
import { scroll } from '@/lib/lottie/icons/scroll'
import { chevronDown } from '@/lib/lottie/icons/chevron-down'
import { edit } from '@/lib/lottie/icons/edit'
import { trash } from '@/lib/lottie/icons/trash'
import { menu } from '@/lib/lottie/icons/creattie/menu'
import { lifebuoy } from '@/lib/lottie/icons/creattie/lifebuoy'
import { feedback } from '@/lib/lottie/icons/creattie/feedback'
import { support } from '@/lib/lottie/icons/creattie/support'

/**
 * Get animation data for a lottie animation
 */
export const animationMap: Record<LottieAnimationName, any> = {
    'send': send,
    'bookmark': bookmark,
    'bookmark-add': bookmarkAdd,
    'bookmark-remove': bookmarkRemove,
    'paperclip': paperclip,
    'sidebar': sidebar,
    'error404': error404,
    'file': file,
    'validate': validate,
    'scroll': scroll,
    'chevron-down': chevronDown,    
    'edit': edit,
    'trash': trash,
    'menu': menu,
    'lifebuoy': lifebuoy,
    'feedback': feedback,
    'support': support,
}
export function getLottieAnimationData(name: LottieAnimationName): any {

    const data = animationMap[name]
    
    if (!data) {
        console.warn(`Animation data not found for "${name}". Make sure to import and register the animation.`)
        return null
    }
    
    return data
}

/**
 * Normalization config for mapping raw lottie content to a target visual size.
 * These values are measured so that at multiplier=1 the icon appears ~16×16
 * inside a 32×32 button tile, centered and consistent across icons.
 */
export interface LottieNormalizationConfig {
    baseScale: number
    offsetX?: number
    offsetY?: number
}
const normalizationMap: Record<LottieAnimationName, LottieNormalizationConfig> = {
    send: { baseScale: 1.35, offsetX: 0, offsetY: 0 },
    bookmark: { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'bookmark-add': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'bookmark-remove': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    paperclip: { baseScale: 1.30, offsetX: 0, offsetY: 0 },
    sidebar: { baseScale: 1.35, offsetX: 0, offsetY: 0 },
    error404: { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    file: { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    validate: { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    scroll: { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'chevron-down': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'edit': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'trash': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'menu': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'lifebuoy': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'feedback': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
    'support': { baseScale: 1.25, offsetX: 0, offsetY: 0 },
}

export function getNormalizationConfig(name: LottieAnimationName): LottieNormalizationConfig {
    return normalizationMap[name] ?? { baseScale: 1, offsetX: 0, offsetY: 0 }
}

export function getNormalizedStyle(
    name: LottieAnimationName,
    targetSize: number = 16,
    multiplier: number = 1
): { width: number; height: number; scale: number; offsetX: number; offsetY: number } {
    const cfg = getNormalizationConfig(name)
    return {
        width: targetSize,
        height: targetSize,
        scale: cfg.baseScale * multiplier,
        offsetX: cfg.offsetX ?? 0,
        offsetY: cfg.offsetY ?? 0
    }
}

export function getButtonContainerStyle(size: number = 32): React.CSSProperties {
    return {
        width: size,
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
    }
}